/*
  A contact's phone numbers, in the order the app shows them, with duplicates removed - and, in
  contactMethods below, where the email sits among them.

  Stated once because two places render them - the Customers dialog's Contacts tab and the Edit
  Customer Contacts dialog - and a contact whose columns disagree would otherwise read differently
  depending on which one you opened.

  Priority is Mobilephone, then Phone, then Business_phone. It decides which label a number keeps
  when two columns hold the SAME number, which is common: a contact with one line often has it
  recorded in several. The two messageable columns come first, so a number that can be chosen as
  the Preferred Channel is never hidden behind one that cannot - Business_phone is a switchboard,
  shown for reference and never messaged, which is what its label says.
*/
export const CONTACT_PHONE_COLUMNS = [
  { field: 'mobilephone', suffix: 'Mobile', channel: 'Mobilephone' },
  { field: 'phone', suffix: 'Phone', channel: 'Phone' },
  { field: 'business_phone', suffix: 'Business (not logged)', channel: null },
] as const;

export type ContactPhone = {
  value: string;
  suffix: string;
  channel: string | null;
};

/* Last 10 digits - what makes "(712) 441-1942" and "712-441-1942" the same number. */
export const phoneDigits = (value?: string | null) => value?.replace(/\D/g, '').slice(-10);

/* The numbers to show for a contact, in priority order, one entry per distinct number. */
export function contactPhones(contact: any): ContactPhone[] {
  const seen = new Set<string>();

  return CONTACT_PHONE_COLUMNS.reduce<ContactPhone[]>((kept, { field, suffix, channel }) => {
    const value = contact?.[field];
    const digits = phoneDigits(value);

    if (!digits || seen.has(digits)) return kept;

    seen.add(digits);
    return [...kept, { value, suffix, channel }];
  }, []);
}

export type ContactMethod = ({ kind: 'phone' } & ContactPhone) | { kind: 'email'; value: string };

/*
  Every way of reaching the contact, in the order both Email/Phone cells list them: the numbers a
  message can go to, then the main email, then the contact's additional emails (HubSpot's
  "Additional email addresses"), then Business_phone.

  The first three are the order the Customers pages message a contact with no Preferred Channel in
  (SceRecipientService.CustomersFirstChannel: Mobilephone, then Phone, then Email), so on those
  pages the top line is the address a message defaults to. SCE Reports messages the email first
  (SceReportsFirstChannel) but shares Edit Contacts, which keeps this order. Business_phone is never
  messaged, so it trails as reference.
*/
export function contactMethods(contact: any): ContactMethod[] {
  const phones = contactPhones(contact).map((phone) => ({ kind: 'phone' as const, ...phone }));
  const emails: string[] = [
    ...(contact?.email ? [contact.email] : []),
    ...(contact?.additionalEmails ?? []),
  ];

  return [
    ...phones.filter(({ channel }) => channel),
    ...emails.map((value) => ({ kind: 'email' as const, value })),
    ...phones.filter(({ channel }) => !channel),
  ];
}

/*
  The email the contact's Preferred marker belongs beside: the main email when Email is preferred,
  or the additional address they chose - which the server sends as preferredChannel itself (an
  address, where otherwise it sends a column name; see SceContactBo.PreferredChannel). Undefined
  when a number or nothing is preferred.
*/
export function preferredEmail(contact: any): string | undefined {
  const preferred = contact?.preferredChannel;

  if (preferred === 'Email') return contact?.email || undefined;

  return preferred?.includes('@') ? preferred : undefined;
}

/*
  The digits of the contact's preferred number, or undefined when an email is preferred or nothing is.

  Resolved to digits rather than compared by column, because the de-dupe above drops whichever
  column repeats a number - matching on the column alone would hide the marker exactly when two
  columns agree.
*/
export function preferredPhoneDigits(contact: any) {
  const preferred = CONTACT_PHONE_COLUMNS.find(
    ({ channel }) => channel && channel === contact?.preferredChannel
  );

  return phoneDigits(preferred && contact?.[preferred.field]);
}
